export interface SSEEvent {
  event: string;
  data: unknown;
}

/**
 * Async generator that reads an SSE stream from a Supabase Edge Function.
 * Yields named SSE events as { event, data } pairs.
 *
 * Event dispatch model:
 * - Each `data:` line immediately yields one event using the current event type.
 * - `event:` lines set the type for the *next* `data:` line, then reset to 'message'.
 * - Blank lines (SSE spec dispatch boundaries) are ignored — dispatch is per data line.
 *
 * Pass a discriminated union type parameter to get typed events:
 *   readSSEStream<MySSEEvent>(...)
 * The caller is responsible for validating event data at runtime.
 */
export async function* readSSEStream<T extends SSEEvent = SSEEvent>(
  functionName: string,
  body: Record<string, unknown>,
  session: { access_token: string },
  supabaseUrl: string,
  signal?: AbortSignal,
): AsyncGenerator<T> {
  const url = `${supabaseUrl}/functions/v1/${functionName}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`SSE request failed: ${response.status} — ${text}`);
  }

  if (!response.body) {
    throw new Error('No response body for SSE stream');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = 'message';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
          continue;
        }

        if (line.startsWith('data: ')) {
          const chunk = line.slice(6);
          let parsed: unknown = chunk;
          try {
            parsed = JSON.parse(chunk);
          } catch {
            // Keep as string if not valid JSON
          }
          yield { event: currentEvent, data: parsed } as T;
          currentEvent = 'message';
          continue;
        }

        // Blank lines are SSE event boundaries — dispatch already happened above.
      }
    }

    // Flush decoder and handle any trailing line without a newline terminator
    const remaining = decoder.decode();
    if (remaining) buffer += remaining;
    if (buffer.startsWith('data: ')) {
      const chunk = buffer.slice(6);
      let parsed: unknown = chunk;
      try {
        parsed = JSON.parse(chunk);
      } catch { /* keep as string */ }
      yield { event: currentEvent, data: parsed } as T;
    }
  } finally {
    reader.releaseLock();
  }
}
